import React, { useMemo } from 'react'
import { FormRenderProps } from 'react-final-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { endOfMonth, format, parse } from 'date-fns'
import { isString } from 'lodash-es'
import styled, { css, useTheme } from 'styled-components/native'

import { FeedbackEntityType } from '@lyrahealth-inc/shared-app-logic/src/models/feedback/Feedback'
import { FieldSchema } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { FlyingEnvelopeLottie } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/flyingEnvelopeLottie/FlyingEnvelopeLottie'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { FormBody } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/formBody/FormBody'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import getCareInsightsFeedbackMetadata from './careInsightsFeedbackMetadata'
import { actions, mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { getAuthSupervisorId, getAuthUserId } from '../../../data/auth/authSelectors'
import { useCreateFeedbackMutation, useGetFeedbackQuery } from '../../clientNotes/data/notesApi'

interface CareInsightsFeedbackSectionProps {
  selectedMonthYear: string
  isSupervisor?: boolean
}

const ContentContainer = styled.View(({ theme }) => ({
  minHeight: 400, // this is so that when the data is loaded, the page does not jump around
  marginBottom: theme.spacing['24px'],
  borderTopWidth: '1px',
  borderTopColor: theme.colors.borderDefault,
}))

const ImageContainer = styled.View(({ theme }) => ({
  marginLeft: 'auto',
  marginRight: 'auto',
  padding: theme.spacing['24px'],
}))

const ImageHeader = styled(Title)(({ theme }) => ({
  textAlign: 'center',
  paddingTop: theme.spacing['8px'],
}))

const ImageText = styled(BodyText)(({ theme }) => ({
  textAlign: 'center',
  paddingTop: theme.spacing['8px'],
  maxWidth: '280px',
  marginLeft: 'auto',
  marginRight: 'auto',
}))

const FormButtonContainer = styled.View(({ theme }) => ({
  paddingTop: theme.spacing['16px'],
  paddingLeft: theme.spacing['16px'],
}))
export const CareInsightsFeedbackSection: React.FC<CareInsightsFeedbackSectionProps> = ({
  selectedMonthYear,
  isSupervisor,
}) => {
  const { colors } = useTheme()
  const intl = useIntl()
  const userId = useSelector(getAuthUserId)
  const supervisorId = useSelector(getAuthSupervisorId)
  const entityId = format(endOfMonth(parse(selectedMonthYear, 'MMMM yyyy', new Date())), 'yyyy-MM') + '-' + userId
  const { data: feedback, isLoading: isLoadingFeedback } = useGetFeedbackQuery({
    providerId: isSupervisor ? supervisorId : userId,
    entityId,
  })

  const feedbackResponse = useMemo(() => {
    if (feedback && feedback.length) {
      return feedback.find((response: { entity_id: string }) => {
        return response.entity_id === entityId
      })
    }
    return undefined
  }, [feedback, entityId])

  const [createFeedback, { isSuccess: isSubmitted }] = useCreateFeedbackMutation()

  const formButton = ({
    handleSubmit,
    pristine,
  }: {
    handleSubmit: FormRenderProps['handleSubmit']
    pristine: boolean
  }) => {
    if (pristine) {
      return <></>
    }

    return (
      <FormButtonContainer>
        <PrimaryButton
          text={<FormattedMessage defaultMessage='Submit' description='Button to submit feedback on Care Insights' />}
          onPress={() => {
            handleSubmit()
          }}
          testID={tID('CareInsightsFeedbackSection-submitFeedbackButton')}
          accessibilityLabel={intl.formatMessage({
            defaultMessage: 'Submit',
            description: 'Button to submit feedback on Care Insights',
          })}
        />
      </FormButtonContainer>
    )
  }

  const { schema, uiSchema } = getCareInsightsFeedbackMetadata({ isSupervisor })

  if (isLoadingFeedback) {
    return <ContentContainer testID={tID('CareInsightsFeedback-loadingContainer')} />
  }

  if (feedbackResponse || isSubmitted) {
    return (
      <ContentContainer testID={tID('CareInsightsFeedback-feedbackSubmittedContainer')}>
        <ImageContainer>
          <FlyingEnvelopeLottie loop={false} />
        </ImageContainer>
        <ImageHeader size={TitleSize.SMALL} text='Feedback submitted' color={colors.textPrimary} />
        <ImageText
          testID={tID('CareInsightsFeedback-feedbackSubmittedText')}
          text='Thank you for the feedback. It will help us improve your experience.'
          size={BodyTextSize.SMALL}
          color={colors.textSecondary}
        />
      </ContentContainer>
    )
  }

  return (
    <ContentContainer testID={tID('CareInsightsFeedback-form')}>
      <FormBody
        name='careInsightsFeedbackForm'
        schema={schema as FieldSchema}
        uiSchema={uiSchema}
        saveForm={({ values }) => {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: actions.REFLECTION_INSIGHTS_SUBMIT_FEEDBACK,
          })
          createFeedback({
            feedback: {
              entity_type: FeedbackEntityType.REFLECTION_INSIGHTS,
              entity_id: entityId,
              lyra_id: isSupervisor ? supervisorId : userId,
              user_type: isSupervisor ? 'supervisor' : 'provider',
              feedback_response: Object.entries(values).map((value: [string, string | number | string[]]) => {
                return { question: value[0], answer: isString(value[1]) ? value[1] : JSON.stringify(value[1]) }
              }),
            },
          })
        }}
        intl={intl}
        scrollContainerCustomStyles={{
          formBodyPageContainer: css`
            max-width: 100%;
          `,
          scrollContainerContentCustomStyles: {
            padding: 0,
          },
        }}
        formBodyCustomStyles={{
          submitButtonWrapper: css`
            align-self: flex-start;
          `,
          submitButtonContainer: css`
            border: none;
            box-shadow: none;
            border-top-width: 0;
            padding: 0;
          `,
        }}
        formButton={formButton}
      />
    </ContentContainer>
  )
}

export default CareInsightsFeedbackSection
